import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useProviderAuth } from '@/context/ProviderAuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Phone, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import { formatPhoneDisplay } from '@/utils/phoneUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  { value: 'assigned', label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' }
];

const LeadDetail = () => {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const { provider, token } = useProviderAuth();
  const [assignment, setAssignment] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchAssignmentDetails();
    fetchComments();
  }, [assignmentId]);

  const fetchAssignmentDetails = async () => {
    try {
      const response = await axios.get(`${API}/api/provider/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundAssignment = response.data.find(a => a.id === assignmentId);
      if (foundAssignment) {
        setAssignment(foundAssignment);
      } else {
        toast.error('Assignment not found');
        navigate('/provider/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch assignment:', error);
      toast.error('Failed to load assignment details');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/api/provider/assignments/${assignmentId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/api/provider/assignments/${assignmentId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssignment({ ...assignment, status: newStatus });
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/api/provider/assignments/${assignmentId}/comments`,
        { comment: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments([...comments, response.data]);
      setNewComment('');
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-stone-500">Loading lead details...</p>
      </div>
    );
  }

  if (!assignment) {
    return null;
  }

  const currentStatus = statusOptions.find(s => s.value === assignment.status);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      <Button
        onClick={() => navigate('/provider/dashboard')}
        variant="ghost"
        className="mb-4"
        data-testid="back-to-dashboard"
      >
        <ArrowLeft size={18} className="mr-2" />
        Back to Dashboard
      </Button>
      <h1 className="text-2xl font-bold text-stone-900 mb-1">Lead Details</h1>
      <p className="text-stone-600 mb-6">Assigned by {assignment.agent_name}</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lead Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visitor Info Card */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                <User size={24} className="text-[#1A4D2E]" />
                Visitor Information
              </h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-stone-600 text-sm">Full Name</Label>
                  <p className="text-lg font-semibold text-stone-900">{assignment.visitor_name}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-600 text-sm flex items-center gap-2">
                      <Mail size={14} />
                      Email
                    </Label>
                    <p className="text-stone-900">{assignment.visitor_email}</p>
                  </div>
                  <div>
                    <Label className="text-stone-600 text-sm flex items-center gap-2">
                      <Phone size={14} />
                      Phone
                    </Label>
                    <p className="text-stone-900">{formatPhoneDisplay(assignment.visitor_phone)}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-stone-600 text-sm flex items-center gap-2">
                    <MapPin size={14} />
                    Property Address
                  </Label>
                  <p className="text-stone-900">{assignment.property_address || 'Not provided'}</p>
                </div>

                {assignment.description && (
                  <div>
                    <Label className="text-stone-600 text-sm">Description</Label>
                    <p className="text-stone-900">{assignment.description}</p>
                  </div>
                )}

                <div>
                  <Label className="text-stone-600 text-sm flex items-center gap-2">
                    <Clock size={14} />
                    Assigned On
                  </Label>
                  <p className="text-stone-900">{formatDate(assignment.created_at)}</p>
                </div>
              </div>
            </Card>

            {/* Comments Section */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                <MessageSquare size={24} className="text-[#1A4D2E]" />
                Comments & Updates
              </h2>

              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="mb-6">
                <div className="flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or update..."
                    className="flex-1"
                    disabled={submitting}
                  />
                  <Button
                    type="submit"
                    disabled={submitting || !newComment.trim()}
                    className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                  >
                    <Send size={18} className="mr-2" />
                    {submitting ? 'Posting...' : 'Post'}
                  </Button>
                </div>
              </form>

              {/* Comments List */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-stone-500">
                    <MessageSquare size={48} className="mx-auto mb-2 text-stone-300" />
                    <p>No comments yet. Be the first to add an update!</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="border-l-4 border-[#1A4D2E] pl-4 py-2 bg-stone-50 rounded-r">
                      <div className="flex items-start justify-between mb-1">
                        <span className="font-semibold text-stone-900">{comment.author_name}</span>
                        <span className="text-xs text-stone-500">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-stone-700">{comment.comment}</p>
                      <span className="text-xs text-stone-500 capitalize">{comment.author_type}</span>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar - Status & Actions */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold text-stone-900 mb-4">Lead Status</h3>
              <div className="space-y-3">
                {statusOptions.map((status) => (
                  <Button
                    key={status.value}
                    onClick={() => handleStatusChange(status.value)}
                    disabled={updatingStatus || assignment.status === status.value}
                    className={`w-full justify-start ${
                      assignment.status === status.value
                        ? 'bg-[#1A4D2E] text-white hover:bg-[#143D24]'
                        : 'bg-white text-stone-700 hover:bg-stone-100 border-2'
                    }`}
                    variant={assignment.status === status.value ? 'default' : 'outline'}
                  >
                    <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
                      assignment.status === status.value ? 'bg-white' : status.color.split(' ')[0]
                    }`} />
                    {status.label}
                  </Button>
                ))}
              </div>
            </Card>

            <Card className="p-6 bg-stone-50">
              <h3 className="text-lg font-bold text-stone-900 mb-3">Assignment Details</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-stone-600">Service Type:</span>
                  <p className="font-semibold text-stone-900">{assignment.service_type}</p>
                </div>
                <div>
                  <span className="text-stone-600">Agent:</span>
                  <p className="font-semibold text-stone-900">{assignment.agent_name}</p>
                </div>
                <div>
                  <span className="text-stone-600">Last Updated:</span>
                  <p className="font-semibold text-stone-900">{formatDate(assignment.updated_at)}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
  );
};

export default LeadDetail;
